import { type ReactNode } from "react";
import { ServiceTitanIconLink } from "@/core/components/service-titan-icon-link";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { sentenceCase } from "./utils";

/** The grey inline card an action's ServiceTitan record (or edit form) sits in. */
export const RECORD_CARD_CLASS = "rounded-md bg-neutral px-3 py-3";

/**
 * A ServiceTitan object link — the Invoices table / sidebar link in
 * interactive-text (green-70, 6.8:1 on white), not the core ST links'
 * primary (green-50, 3.3:1, below AA at 13px).
 */
export const StLink = ({
  url,
  label,
  children,
}: {
  url: string;
  label: string;
  children: ReactNode;
}) => (
  <ServiceTitanIconLink url={url} label={label}>
    {children}
  </ServiceTitanIconLink>
);

export const StRecord = ({
  label,
  isNew,
  link,
  name,
  children,
}: {
  label: string;
  /** True when the agent is creating this record in ServiceTitan rather than referencing an existing one. */
  isNew?: boolean;
  link?: ReactNode;
  /** The record's name when there's no link to show. */
  name?: ReactNode;
  children?: ReactNode;
}) => (
  <Stack align="start" gap={4} className={cn("w-full", RECORD_CARD_CLASS)}>
    <RecordField label={sentenceCase(isNew ? `New ${label}` : label)}>
      {link ?? name}
    </RecordField>
    {children}
  </Stack>
);

/**
 * A label / value pair inside a record card: core `InfoWithLabel`'s label
 * (13px semibold tertiary) over a 13px value, so values never outrank the
 * action's own 13px title.
 */
export const RecordField = ({
  label,
  fullWidth,
  className,
  children,
}: {
  label: string;
  fullWidth?: boolean;
  className?: string;
  children: ReactNode;
}) => (
  <Stack
    align="start"
    gap={1}
    className={cn(cn("min-w-0", className), { "w-full": fullWidth })}
  >
    <Text variant="label" tone="muted">
      {label}
    </Text>
    {typeof children === "string" ? (
      <Text className="wrap-break-word">{children}</Text>
    ) : (
      children
    )}
  </Stack>
);

/** Short fields side by side; three columns so they line up across cards. */
export const RecordFieldGrid = ({ children }: { children: ReactNode }) => (
  <Stack
    align="start"
    gap={0}
    className="grid w-full grid-cols-3 gap-x-4 gap-y-3"
  >
    {children}
  </Stack>
);

/** Card for actions that touch no ServiceTitan object. */
export const NoStCard = ({ reason }: { reason: string }) => (
  <Stack align="start" gap={0} className={cn("w-full", RECORD_CARD_CLASS)}>
    <RecordField label="No ServiceTitan change">{reason}</RecordField>
  </Stack>
);
